/**
 * BrandPage — "Manual de tu IA"
 *
 * Rediseño con tabs (antes era scroll infinito de 5 secciones):
 *
 *   Hero compacto + Save bar
 *
 *   [Identidad] [Personalidad] [Seguridad] [Probador]
 *
 *   Footer: banda Impacto 30 días
 *
 * Cambios conceptuales:
 *   - SACA presupuesto USD (no aplica en QR — mensajes son gratis)
 *   - SACA ROI mínimo y Fairness cap (concepts financieros enterprise raros
 *     para PyMEs gastronómicas)
 *   - MUEVE frequency cap a tab Seguridad (junto a palabras y horario)
 *   - SEPARA Identidad (datos de la marca + Learning) de Personalidad (sliders)
 *   - El Simulador deja la sidebar y gana su propia tab
 *
 * La tab activa persiste en URL ?tab=identity para que refresh/share sirva.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Loader2,
    Save,
    Check,
    AlertCircle,
    User,
    Mic,
    ShieldAlert,
    FlaskConical,
    Info,
} from "lucide-react";
import BrandHero from "../components/BrandHero";
import BrandIdentityTab from "../components/BrandIdentityTab";
import RuleSliders from "../components/RuleSliders";
import SecurityCards from "../components/SecurityCards";
import BrandSimulator from "../components/BrandSimulator";
import { Button } from "../../../shared/components/ui/Button";
import { brandRulesApi, type FrequencyCap } from "../api";
import { useIntegracion } from "../../../hooks/useIntegracion";
import { toast } from "../../../shared/hooks/useToast";

const MOCK_BRAND_ID = "brand-demo";

type TabKey = "identity" | "personality" | "security" | "simulator";

const TABS: { key: TabKey; label: string; icon: typeof User }[] = [
    { key: "identity", label: "Identidad", icon: User },
    { key: "personality", label: "Personalidad", icon: Mic },
    { key: "security", label: "Seguridad", icon: ShieldAlert },
    { key: "simulator", label: "Probador", icon: FlaskConical },
];

export default function BrandPage() {
    /* ─── State editable ─── */
    const [tone, setTone] = useState(65);
    const [aggressiveness, setAggressiveness] = useState(35);
    const [forbiddenWords, setForbiddenWords] = useState<string[]>([
        "garantizado",
        "100% seguro",
        "sin riesgo",
    ]);
    const [startTime, setStartTime] = useState("10:00");
    const [endTime, setEndTime] = useState("21:00");
    const [frequencyCap, setFrequencyCap] = useState<FrequencyCap>("1_week");

    /* ─── State no editable (hardcoded — antes era UI, ahora se manda fijo) ─── */
    // Mantenemos en el payload del API para backwards-compat con el schema, pero
    // la UI ya no los muestra. Si en el futuro se quieren reactivar, hay un
    // único lugar para tocar.
    const [maxBudget] = useState(0);
    const [minRoi] = useState(0);
    const [fairnessCap] = useState(0);

    /* ─── UI state ─── */
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<Date | null>(null);
    const [dirty, setDirty] = useState(false);
    // Sin esta flag, el batch de setState del load disparaba el useEffect de
    // dirty y dejaba "Cambios sin guardar" desde el primer render.
    const hasHydrated = useRef(false);

    /* ─── Tab state — persiste en URL ?tab=identity ─── */
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab") as TabKey | null;
    const activeTab: TabKey =
        tabParam && TABS.find((t) => t.key === tabParam)
            ? tabParam
            : "identity";
    const setTab = (t: TabKey) => {
        searchParams.set("tab", t);
        setSearchParams(searchParams, { replace: true });
    };

    /* ─── Brand identity (read-only from CRM) ─── */
    const { marca, getMarca } = useIntegracion();

    /* ─── Load rules on mount ─── */
    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await brandRulesApi.get(MOCK_BRAND_ID);
            if (res.success && res.data?.brandId) {
                const r = res.data;
                setTone(r.tone);
                setAggressiveness(r.aggressiveness);
                setForbiddenWords(r.forbiddenWords);
                setStartTime(r.sendWindow.start);
                setEndTime(r.sendWindow.end);
                setFrequencyCap(r.frequencyCap);
            } else if (!res.success) {
                setError(res.error || "No se pudieron cargar las reglas");
            }
            setLoading(false);
            setDirty(false);
            // Activar listener en el siguiente tick para que no capture el
            // batch de setState del propio load.
            queueMicrotask(() => {
                hasHydrated.current = true;
            });
        })();
        if (!marca) getMarca();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ─── Mark dirty on change ─── */
    useEffect(() => {
        if (!hasHydrated.current) return;
        setDirty(true);
    }, [
        tone,
        aggressiveness,
        forbiddenWords,
        startTime,
        endTime,
        frequencyCap,
    ]);

    /* ─── Save ─── */
    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        const res = await brandRulesApi.upsert({
            brandId: MOCK_BRAND_ID,
            tone,
            aggressiveness,
            forbiddenWords,
            sendWindow: { start: startTime, end: endTime },
            frequencyCap,
            // Sin UI — siempre 0 (sin restricción en QR mode)
            maxBudgetUSD: maxBudget,
            minRoi,
            fairnessCap,
        });
        setSaving(false);
        if (res.success) {
            setSavedAt(new Date());
            setDirty(false);
            toast.success("Manual de IA guardado");
        } else {
            const msg = res.error || "Error al guardar";
            setError(msg);
            toast.error(msg);
        }
    }, [
        tone,
        aggressiveness,
        forbiddenWords,
        startTime,
        endTime,
        frequencyCap,
        maxBudget,
        minRoi,
        fairnessCap,
    ]);

    /* ─── BrandLearning callback ─── */
    const handleApplyLearning = useCallback(
        (r: {
            tone: number;
            aggressiveness: number;
            addForbidden: string[];
        }) => {
            setTone(r.tone);
            setAggressiveness(r.aggressiveness);
            setForbiddenWords((prev) => {
                const merged = new Set([...prev, ...r.addForbidden]);
                return Array.from(merged);
            });
            // Auto-saltar a la tab Personalidad para que el user vea el efecto
            setTab("personality");
            // Audit V4-17: feedback explícito (antes saltaba de tab sin avisar)
            toast.success("Estilo aplicado — revisalo en Personalidad");
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-500">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Cargando manual…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-5">
                {/* ── Hero compacto ── */}
                <BrandHero
                    marca={marca}
                    tone={tone}
                    aggressiveness={aggressiveness}
                    sendWindow={{ start: startTime, end: endTime }}
                />

                {/* ── Tabs + Save bar combinados ── */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden sticky top-2 z-30">
                    <div className="flex items-center justify-between">
                        {/* Tabs */}
                        <nav className="flex items-center overflow-x-auto">
                            {TABS.map((t) => {
                                const Icon = t.icon;
                                const isActive = activeTab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        onClick={() => setTab(t.key)}
                                        className={`relative flex items-center gap-2 px-4 md:px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
                                            isActive
                                                ? "text-violet-700"
                                                : "text-slate-500 hover:text-slate-800"
                                        }`}
                                    >
                                        <Icon size={15} />
                                        {t.label}
                                        {isActive && (
                                            <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-violet-600 rounded-t-full" />
                                        )}
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Save status + button */}
                        <div className="flex items-center gap-3 pr-3">
                            {savedAt && !dirty && (
                                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
                                    <Check size={13} />
                                    Guardado
                                </span>
                            )}
                            {dirty && (
                                <span className="text-xs text-amber-600 font-medium hidden sm:inline">
                                    Cambios sin guardar
                                </span>
                            )}
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={saving || !dirty}
                            >
                                {saving ? (
                                    <>
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                        Guardando…
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Guardar
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                        <AlertCircle size={18} />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                )}

                {/* ── Tab content ── */}
                <div className="animate-fade-in">
                    {activeTab === "identity" && (
                        <BrandIdentityTab
                            marca={marca}
                            onApplyLearning={handleApplyLearning}
                        />
                    )}

                    {activeTab === "personality" && (
                        <div className="space-y-4">
                            <SectionTitle
                                title="Cómo habla tu IA"
                                subtitle="Ajustá el tono y la estrategia. La IA respeta estos valores en todas las plantillas y mensajes generados."
                            />
                            <RuleSliders
                                tone={tone}
                                setTone={setTone}
                                aggressiveness={aggressiveness}
                                setAggressiveness={setAggressiveness}
                            />
                        </div>
                    )}

                    {activeTab === "security" && (
                        <div className="space-y-4">
                            <SectionTitle
                                title="Qué NO puede hacer la IA"
                                subtitle="Restricciones de contenido, horario y frecuencia que protegen tu marca y evitan bans de WhatsApp."
                            />
                            <SecurityCards
                                forbiddenWords={forbiddenWords}
                                setForbiddenWords={setForbiddenWords}
                                startTime={startTime}
                                setStartTime={setStartTime}
                                endTime={endTime}
                                setEndTime={setEndTime}
                                frequencyCap={frequencyCap}
                                setFrequencyCap={(v: string) =>
                                    setFrequencyCap(v as FrequencyCap)
                                }
                            />
                        </div>
                    )}

                    {activeTab === "simulator" && (
                        <div className="space-y-4">
                            <SectionTitle
                                title="Probá tu IA en vivo"
                                subtitle="18 escenarios reales (campañas de efeméride + comportamentales) con preview WhatsApp y Push. Cambiá los sliders en Personalidad y volvé acá a probar."
                            />
                            {/* Instrucciones de uso del simulador */}
                            <div className="max-w-3xl mx-auto bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                                <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-blue-700 leading-relaxed">
                                    <strong>Cómo usarlo:</strong> Hacé click en un escenario de la lista para ver el mensaje que generaría tu IA.{" "}
                                    Cambiá el canal entre WhatsApp y Push para comparar formatos.{" "}
                                    Si ajustás los sliders en la tab <strong>Personalidad</strong> y volvés acá, los mensajes se actualizan al instante.
                                </p>
                            </div>
                            <div className="max-w-3xl mx-auto">
                                <BrandSimulator
                                    tone={tone}
                                    aggressiveness={aggressiveness}
                                    forbiddenWords={forbiddenWords}
                                    brandName={marca?.appName}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ─── Sub: title de sección dentro de cada tab ─── */
function SectionTitle({
    title,
    subtitle,
}: {
    title: string;
    subtitle: string;
}) {
    return (
        <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                {title}
            </h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                {subtitle}
            </p>
        </div>
    );
}
